
import { useState } from 'react';
import { hireEquipment, hireCategories, HireItem } from '../../../mocks/hireEquipment';
import { InvoiceLineItem } from '../page';

interface EquipmentSelectorProps {
  lineItems: InvoiceLineItem[];
  onAdd: (item: HireItem) => void;
  onRemove: (lineId: string) => void;
  onUpdateQty: (lineId: string, qty: number) => void;
  onUpdateDays: (lineId: string, days: number) => void;
}

export default function EquipmentSelector({ lineItems, onAdd, onRemove, onUpdateQty, onUpdateDays }: EquipmentSelectorProps) {
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  const filtered = hireEquipment.filter((item) => {
    const matchCat = activeCategory === 'All' || item.category === activeCategory;
    const matchSearch = item.name.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const getLinesForItem = (itemId: string) => lineItems.filter((li) => li.item.id === itemId);

  return (
    <div>
      {/* Search */}
      <div className="relative mb-5">
        <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 text-sm"></i>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search equipment..."
          className="w-full pl-10 pr-4 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
        />
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {hireCategories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeCategory === cat
                ? 'bg-gradient-to-r from-amber-500 to-amber-400 text-white shadow-sm'
                : 'bg-stone-100 text-stone-500 hover:text-stone-700 hover:bg-stone-200'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Equipment Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[520px] overflow-y-auto pr-1 custom-scrollbar">
        {filtered.map((item) => {
          const itemLines = getLinesForItem(item.id);
          const hasLines = itemLines.length > 0;
          return (
            <div
              key={item.id}
              className={`relative rounded-xl border p-3 transition-all ${
                hasLines ? 'border-amber-400 bg-amber-50/50 shadow-sm' : 'border-stone-200 bg-white hover:border-amber-300 hover:shadow-sm'
              }`}
            >
              {/* Promo Badge */}
              {item.promo && (
                <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-gradient-to-r from-emerald-500 to-emerald-400 text-white text-[9px] font-bold rounded-full shadow-md z-10">
                  $1 PROMO
                </div>
              )}

              <div className="flex gap-3">
                <div className="w-16 h-16 rounded-lg bg-stone-100 overflow-hidden flex-shrink-0">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-amber-600 font-medium">{item.category}</p>
                  <p className="text-sm font-semibold text-stone-800 truncate">{item.name}</p>
                  <p className="text-sm font-bold text-stone-900">
                    ${item.price.toFixed(2)} <span className="text-xs font-normal text-stone-400">{item.unit}</span>
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {item.minQty > 1 && <p className="text-[10px] text-stone-400">Min. {item.minQty}</p>}
                    {item.maxQty && <p className="text-[10px] text-amber-600 font-medium">Max. {item.maxQty}</p>}
                  </div>
                </div>
              </div>

              {/* Existing line items for this equipment */}
              {hasLines && (
                <div className="mt-2.5 space-y-2">
                  {itemLines.map((lineItem, lineIdx) => (
                    <div key={lineItem.lineId} className="flex items-center gap-2 bg-white/70 rounded-lg p-1.5 border border-amber-200/50">
                      {itemLines.length > 1 && (
                        <span className="text-[9px] text-amber-500 font-bold w-4 text-center">#{lineIdx + 1}</span>
                      )}
                      <div className="flex items-center gap-1 flex-1">
                        <label className="text-[10px] text-stone-500 w-7">Qty</label>
                        <div className="flex items-center border border-stone-200 rounded-md overflow-hidden">
                          <button
                            onClick={() => onUpdateQty(lineItem.lineId, Math.max(item.minQty, lineItem.qty - 1))}
                            className="w-6 h-6 flex items-center justify-center text-stone-500 hover:bg-stone-100 cursor-pointer text-xs"
                          >-</button>
                          <input
                            type="number"
                            value={lineItem.qty}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || item.minQty;
                              const maxVal = item.maxQty || 9999;
                              onUpdateQty(lineItem.lineId, Math.min(maxVal, Math.max(item.minQty, val)));
                            }}
                            className="w-12 h-6 text-center text-xs border-x border-stone-200 focus:outline-none"
                          />
                          <button
                            onClick={() => {
                              const maxVal = item.maxQty || 9999;
                              onUpdateQty(lineItem.lineId, Math.min(maxVal, lineItem.qty + 1));
                            }}
                            className="w-6 h-6 flex items-center justify-center text-stone-500 hover:bg-stone-100 cursor-pointer text-xs"
                          >+</button>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-1">
                        <label className="text-[10px] text-stone-500 w-7">Days</label>
                        <div className="flex items-center border border-stone-200 rounded-md overflow-hidden">
                          <button
                            onClick={() => onUpdateDays(lineItem.lineId, Math.max(1, lineItem.days - 1))}
                            className="w-6 h-6 flex items-center justify-center text-stone-500 hover:bg-stone-100 cursor-pointer text-xs"
                          >-</button>
                          <input
                            type="number"
                            value={lineItem.days}
                            onChange={(e) => onUpdateDays(lineItem.lineId, Math.max(1, parseInt(e.target.value) || 1))}
                            className="w-10 h-6 text-center text-xs border-x border-stone-200 focus:outline-none"
                          />
                          <button
                            onClick={() => onUpdateDays(lineItem.lineId, lineItem.days + 1)}
                            className="w-6 h-6 flex items-center justify-center text-stone-500 hover:bg-stone-100 cursor-pointer text-xs"
                          >+</button>
                        </div>
                      </div>
                      <button
                        onClick={() => onRemove(lineItem.lineId)}
                        className="w-6 h-6 flex items-center justify-center rounded-md text-rose-400 hover:bg-rose-50 hover:text-rose-600 cursor-pointer transition-colors"
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add button - always visible to allow multiple */}
              <button
                onClick={() => onAdd(item)}
                className={`mt-2.5 w-full py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  hasLines
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-300'
                    : 'bg-stone-900 text-white hover:bg-amber-600'
                }`}
              >
                <i className="ri-add-line mr-1"></i>{hasLines ? 'Add Another' : 'Add to Invoice'}
              </button>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-2 py-12 text-center text-stone-400 text-sm">
            <i className="ri-search-line text-2xl mb-2 block"></i>
            No equipment found
          </div>
        )}
      </div>
    </div>
  );
}
